import React, { useCallback, useState, useMemo } from 'react';
import classnames from 'classnames';
import BillingInputComponent from '../../template-blocks/billing-input/billing-input-component';
import billingInputTypes from '../../template-blocks/billing-input/billing-input-types';
import Button from 'monday-ui-react-core/dist/Button';

import { v4 as uuid } from 'uuid';

import './billing-items-component.scss';
import BillingItemComponent from './components/billing-item-component/billing-item-component';
import AdjustmentItemComponent from './components/adjustment-item-component/adjustment-item-component';
import AdjustmentMenuComponent from './components/adjustment-menu-component/adjustment-menu-component';
import { calculateFinalPriceByAdjustments } from './billing-items-service';
import currencies from '../../../constants/currencies';
import taxes from '../../../constants/taxes';
import adjustments from '../../../constants/adjustments';
import { useSettings } from '../../../contexts/settings-context';

export default function BillingItemsComponent({
  id,
  items,
  currency,
  data,
  orderData,
  onTemplateDataChange,
  onOrderDataChange,
  previewMode,
  rates,
}) {
  const savedItems = orderData.items?.length > 0 ? orderData.items : items;

  const [currentItems, setCurrentItems] = useState(savedItems);
  const [latestAdjustmentId, setLatestAdjustmentId] = useState(null);
  const { settings } = useSettings();

  const savedRates = useMemo(() => (orderData.rates?.length > 0 ? orderData.rates : rates), [rates, orderData?.rates]);

  const currencySign = useMemo(() => {
    const settingValue = settings?.currency?.value;
    return currencies[settingValue]?.sign || settingValue;
  }, [settings]);

  const taxLabel = useMemo(() => {
    return settings?.tax?.value || taxes.vat.label;
  }, [settings]);

  const feeLabel = useMemo(() => {
    return settings?.fee?.value || adjustments.FEE_DEFAULT_LABEL;
  }, [settings]);

  const totalAmount = useMemo(() => {
    let totalPrice = savedItems.reduce((currentValue, item) => {
      return currentValue + item.quantity * item.price;
    }, 0);

    return calculateFinalPriceByAdjustments(totalPrice, savedRates);
  }, [savedItems, orderData, savedRates]);

  const onAdjustmentAdd = useCallback(
    (rate) => {
      const tmpRates = [...savedRates];

      const newRates = [...tmpRates, rate];

      setLatestAdjustmentId(rate.id);
      onOrderDataChange(id, { ...orderData, rates: newRates });
    },
    [id, onOrderDataChange, orderData, savedRates]
  );

  const onAdjustmentDelete = useCallback(
    (rateId) => {
      const tmpRates = [...savedRates];

      const newRates = tmpRates.filter(({ id }) => id !== rateId);
      onOrderDataChange(id, { ...orderData, rates: newRates });
    },
    [id, onOrderDataChange, orderData, savedRates]
  );

  const onAdjustmentTitleChange = useCallback(
    (rateId, title = '') => {
      const newRates = [...savedRates];

      const rateIndex = newRates.findIndex(({ id }) => id === rateId);

      const rate = newRates[rateIndex];
      newRates[rateIndex] = { ...rate, title };

      onOrderDataChange(id, { ...orderData, rates: newRates });
    },
    [id, onOrderDataChange, orderData, savedRates]
  );

  const onAdjustmentValueChange = useCallback(
    (rate) => {
      const newRates = [...savedRates];

      const rateIndex = newRates.findIndex(({ id: rateId }) => rateId === rate.id);
      if (rateIndex > -1) {
        const currentRate = newRates[rateIndex];
        newRates[rateIndex] = { ...currentRate, ...rate };
      } else {
        newRates.push(rate);
      }

      onOrderDataChange(id, { ...orderData, rates: newRates });
    },
    [id, onOrderDataChange, orderData, savedRates]
  );

  const onOrderItemBillingInputChange = useCallback(
    (itemId, columnChangedData) => {
      const newItems = [...currentItems];
      newItems.forEach((item) => {
        if (item.id === itemId) {
          item[columnChangedData.inputId] = columnChangedData.inputData;
        }
      });
      setCurrentItems(newItems);

      onOrderDataChange(id, { ...orderData, items: newItems });
    },
    [currentItems, setCurrentItems, onOrderDataChange, id, orderData]
  );

  const onOrderItemDelete = useCallback(
    (itemId) => {
      let newItems;
      if (currentItems.length > 1) {
        newItems = currentItems.filter((item) => item.id !== itemId);
      } else {
        newItems = [{ id: uuid(), description: '', quantity: '', price: '' }];
      }

      setCurrentItems(newItems);
      onOrderDataChange(id, { ...orderData, items: newItems });
    },
    [currentItems, id, onOrderDataChange, orderData]
  );

  const addNewItem = useCallback(() => {
    const newItems = [...currentItems, { id: uuid(), description: '', quantity: '', price: '' }];
    setCurrentItems(newItems);
  }, [currentItems, setCurrentItems]);

  const renderHeader = () => {
    return (
      <div className="row header pdf-row">
        <div className="col item-description">Item</div>
        <div className="col item-quantity">Quantity</div>
        <div className="col item-price">Price</div>
        <div className="col item-total">Total</div>
        {!previewMode && <div className="col item-delete"></div>}
      </div>
    );
  };

  const renderItem = ({ id, description, quantity, price }) => {
    return (
      <BillingItemComponent
        key={id}
        id={id}
        description={description}
        quantity={quantity}
        price={price}
        onOrderItemBillingInputChange={onOrderItemBillingInputChange}
        previewMode={previewMode}
        onOrderItemDelete={onOrderItemDelete}
        currencySign={currencySign}
      />
    );
  };

  const renderItems = () => {
    const items = previewMode ? savedItems : currentItems;
    return items.map((item) => renderItem(item));
  };

  const renderAddNewItem = () => {
    return (
      <div className="row add-new-item">
        <Button className="label" size={Button.sizes.SMALL} kind={Button.kinds.SECONDARY} onClick={addNewItem}>
          + Add line item
        </Button>
      </div>
    );
  };

  const renderItemsSection = () => {
    return (
      <div className="items-section">
        {renderHeader()}
        {renderItems()}
        {!previewMode && renderAddNewItem()}
      </div>
    );
  };

  const renderAdjustmentsMenu = () => {
    if (previewMode) return null;

    return <AdjustmentMenuComponent taxLabel={taxLabel} feeLabel={feeLabel} onAdjustmentAdd={onAdjustmentAdd} />;
  };

  const renderAdjustment = (rate) => {
    return (
      <AdjustmentItemComponent
        key={rate.id}
        previewMode={previewMode}
        currency={currency}
        currencySign={currencySign}
        onAdjustmentValueChange={onAdjustmentValueChange}
        onAdjustmentDelete={onAdjustmentDelete}
        onAdjustmentTitleChange={onAdjustmentTitleChange}
        onTitleBlur={() => setLatestAdjustmentId(null)}
        isNew={latestAdjustmentId === rate.id}
        {...rate}
      />
    );
  };

  const renderAdjustments = () => {
    if (!savedRates?.length) return null;

    return <div className="billing-items-adjustments-container">{savedRates.map(renderAdjustment)}</div>;
  };

  const renderEditableTotalSection = () => {
    return (
      <div className="items-section adjustment-section">
        {renderAdjustmentsMenu()}
        <div className="total-section">
          {renderAdjustments()}
          <div className="row total pdf-row">
            <div className="label">Total</div>
            <div className="input-wrapper">
              <BillingInputComponent
                previewMode={previewMode}
                withTitle={false}
                disabled={true}
                type={billingInputTypes.text}
                placeholder="0.00"
                value={`${currencySign}${totalAmount}`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classnames('billing-items-component', { preview: previewMode })}>
      {renderItemsSection()}
      {renderEditableTotalSection()}
    </div>
  );
}
